import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CommentCard from "@/features/comunicacao/components/CommentCard";
import DescricaoEditor from "@/features/comunicacao/components/DescricaoEditor";
import * as cronRepo from "@/features/cronograma/services/cronogramaComentariosRepository";
import * as diarioRepo from "@/features/diario/services/diarioComentariosRepository";
import { supabase } from "@/features/sistema/services/supabaseClient";

type Props = {
  obraId: string;
  /** Recurso genérico: 'relatorio' (padrão) ou 'cronograma_subetapa' */
  resource?: 'relatorio' | 'cronograma_subetapa';
  /** ID do recurso alvo (quando não for relatorio). Continua aceitando relatorioId por compatibilidade. */
  resourceId?: string;
  /** Compat: ID do relatório (caso continue vindo dos formulários antigos) */
  relatorioId?: string;
  dark: boolean;
  allowCreate?: boolean;
  allowEdit?: boolean;
  allowDelete?: boolean;
  /** Controla o "tom"/fundo do cartão. 'white' para páginas claras do Cliente; default mantém o padrão do app. */
  tone?: 'background' | 'white';
};

export default function DiarioComentariosPanel({
  obraId,
  resource = 'relatorio',
  resourceId,
  relatorioId, // retrocompat
  dark,
  allowCreate = true,
  allowEdit = true,
  allowDelete = true,
  tone = 'background',
}: Props) {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [comments, setComments] = useState<diarioRepo.ComentarioRow[]>([]);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const [usersMap, setUsersMap] = useState<Record<string, string>>({});
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [mentionUsers, setMentionUsers] = useState<Array<{ id: string; nome: string; email: string }>>([]);
  const [likesMap, setLikesMap] = useState<Record<string, { count: number; liked: boolean }>>({});
  const effectiveResourceId = resourceId ?? relatorioId ?? "";
  // Ordem crescente: o comentário mais novo fica por último - colado ao editor (que está embaixo)
  const ordered = useMemo(() => {
    return [...comments].sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return ta - tb; // mais antigo primeiro
    });
  }, [comments]);

  const loadLikes = useCallback(
    async (ids: string[]) => {
      if (!ids?.length) return;
      const table = "app_comment_likes";
      const commentField = "comment_id";

      const { data } = await supabase
        .from(table)
        .select(`${commentField}, usuario_id`)
        .in(commentField, ids);

      const byId: Record<string, { count: number; liked: boolean }> = {};
      ids.forEach((id) => {
        byId[id] = { count: 0, liked: false };
      });
      (data ?? []).forEach((row: any) => {
        const commentId = row[commentField];
        if (!commentId) return;
        if (!byId[commentId]) byId[commentId] = { count: 0, liked: false };
        byId[commentId].count += 1;
        if (row.usuario_id && currentUserId && row.usuario_id === currentUserId) {
          byId[commentId].liked = true;
        }
      });
      setLikesMap(byId);
    },
    [currentUserId]
  );

  useEffect(() => {
    if (!obraId || !effectiveResourceId) return;
    let active = true;
    (async () => {
      try {
        let thread:
          | Awaited<ReturnType<typeof cronRepo.ensureThread>>
          | Awaited<ReturnType<typeof diarioRepo.ensureThread>>;
        let data:
          | Awaited<ReturnType<typeof cronRepo.listComments>>
          | Awaited<ReturnType<typeof diarioRepo.listComments>>;

        if (resource === "cronograma_subetapa") {
          thread = await cronRepo.ensureThread(obraId, effectiveResourceId);
          data = await cronRepo.listComments(thread.id, { currentUsuarioId: currentUserId });
        } else {
          thread = await diarioRepo.ensureThread(obraId, effectiveResourceId);
          data = await diarioRepo.listComments(thread.id, { currentUsuarioId: currentUserId });
        }

        if (!active) return;
        setThreadId(thread.id);
        const commentList = (data ?? []) as diarioRepo.ComentarioRow[];
        setComments(commentList);
        if (commentList.length) {
          await loadLikes(commentList.map((c) => c.id));
        }
      } catch (error) {
        // Erro silencioso - comentários podem não estar disponíveis
      }
    })();

    return () => {
      active = false;
    };
  }, [obraId, effectiveResourceId, loadLikes, resource, currentUserId]);

  // Sempre que a lista mudar, atualiza o mapa de autores (inclui quem acabou de comentar)
  useEffect(() => {
    (async () => {
      const ids = Array.from(new Set((comments ?? []).map((c) => c.created_by).filter(Boolean))) as string[];
      if (ids.length) {
        const { data: users } = await supabase.from("usuarios").select("id, nome").in("id", ids);
        if (users) setUsersMap(Object.fromEntries(users.map((u) => [u.id, u.nome || "Usuário"])));
      }
      // carregar papéis da obra (para menções): supervisor/coordenador/estagiário
      if (obraId) {
        const { data: o } = await supabase
          .from("obras")
          .select("supervisor_id, supervisor_nome, coordenador_id, coordenador_nome, estagiario_id, estagiario_nome")
          .eq("id", obraId)
          .maybeSingle();
        const extras: Array<{ id: string; nome: string; email: string }> = [];
        if (o?.supervisor_id && o?.supervisor_nome) extras.push({ id: o.supervisor_id, nome: o.supervisor_nome, email: "" });
        if (o?.coordenador_id && o?.coordenador_nome) extras.push({ id: o.coordenador_id, nome: o.coordenador_nome, email: "" });
        if (o?.estagiario_id && o?.estagiario_nome) extras.push({ id: o.estagiario_id, nome: o.estagiario_nome, email: "" });
        // + autores já presentes na thread
        const fromAuthors = Object.entries(usersMap).map(([id, nome]) => ({ id, nome, email: "" }));
        
        // ✅ NOVO: Carregar TODOS os usuários ativos (exceto clientes) - como nas tarefas
        let todosUsuariosAtivos: Array<{ id: string; nome: string; email: string }> = [];
        try {
          const { data: usuariosAtivos, error: usuariosError } = await supabase
            .from("usuarios")
            .select("id, nome, email, role, funcao")
            .eq("status", "ativo")
            .neq("role", "cliente")
            .neq("funcao", "cliente")
            .order("nome");
          
          if (!usuariosError && usuariosAtivos) {
            todosUsuariosAtivos = usuariosAtivos.map((u) => ({
              id: u.id,
              nome: u.nome || "Usuário",
              email: u.email || ""
            }));
          }
        } catch (_) {
          // Se falhar, continua sem todos os usuários
        }
        
        const dedup = new Map<string, { id: string; nome: string; email: string }>();
        // ✅ Priorizar: primeiro extras (supervisor/coordenador/estagiário/membros da obra), depois autores, depois todos os ativos
        [...extras, ...fromAuthors, ...todosUsuariosAtivos].forEach((u) => {
          if (u.id) dedup.set(u.id, u);
        });

        // Filtrar clientes - buscar role/funcao dos usuários coletados
        const todosUsuariosIds = Array.from(dedup.keys());
        if (todosUsuariosIds.length > 0) {
          const { data: usuariosValidacao } = await supabase
            .from("usuarios")
            .select("id, role, funcao")
            .in("id", todosUsuariosIds);

          // Filtrar apenas usuários que NÃO são clientes
          const usuariosFiltrados: Array<{ id: string; nome: string; email: string }> = [];
          dedup.forEach((usuario, id) => {
            const validacao = usuariosValidacao?.find((u) => u.id === id);
            // Incluir apenas se NÃO for cliente (em role ou funcao)
            if (validacao && validacao.role !== 'cliente' && validacao.funcao !== 'cliente') {
              usuariosFiltrados.push(usuario);
            }
          });
          setMentionUsers(usuariosFiltrados);
        } else {
          setMentionUsers(Array.from(dedup.values()));
        }
      }
      // sempre que a lista de comentários mudar, (re)carrega likes
      const idsForLikes = (comments ?? []).map((c) => c.id);
      if (idsForLikes.length) await loadLikes(idsForLikes);
    })();
  }, [comments, obraId, loadLikes, usersMap]);

  // Carregar usuário atual
  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (auth.user?.id) setCurrentUserId(auth.user.id);
    })();
  }, []);

  // Escuta mudanças e mantém a lista sincronizada (curtidas, edições, novos comentários)
  useEffect(() => {
    if (!threadId) return;
    const reload = async () => {
      let data:
        | Awaited<ReturnType<typeof cronRepo.listComments>>
        | Awaited<ReturnType<typeof diarioRepo.listComments>>;
      if (resource === "cronograma_subetapa") {
        data = await cronRepo.listComments(threadId);
      } else {
        data = await diarioRepo.listComments(threadId, { currentUsuarioId: currentUserId });
      }
      const nextComments = (data ?? []) as diarioRepo.ComentarioRow[];
      setComments(nextComments);
      if (nextComments.length) {
        await loadLikes(nextComments.map((c) => c.id));
      }
    };
    const channel = supabase
      .channel(`thread-${threadId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'app_comments', filter: `thread_id=eq.${threadId}` },
        reload
      )
      // curtidas (INSERT/DELETE) - atualiza apenas o mapa de likes
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'app_comment_likes' },
        async (payload) => {
          const cid = ((payload.new as any)?.comentario_id ?? (payload.old as any)?.comentario_id) as string | undefined;
          if (!cid) return;
          // só reage se o comentário estiver na tela
          const onScreenIds = new Set((comments ?? []).map((c) => c.id));
          if (!onScreenIds.has(cid)) return;
          await loadLikes([cid]);
        }
      )
      .subscribe();
    const onVis = () => {
      if (!document.hidden) reload();
    };
    document.addEventListener('visibilitychange', onVis);
    const timer = setInterval(reload, 15000);
    return () => {
      channel.unsubscribe();
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(timer);
    };
  }, [comments, currentUserId, loadLikes, resource, threadId]);

  // Scroll para ?comentario=
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const target = q.get("comentario");
    if (!target) return;
    // Se existir, rola até o comentário; se não existir mais (após exclusão), limpamos o parâmetro.
    const el = containerRef.current?.querySelector<HTMLElement>(`[data-comment-id="${target}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 1500);
    } else {
      const qs = new URLSearchParams(window.location.search);
      qs.delete("comentario");
      const next = qs.toString();
      // Mantém o usuário no MESMO diário, apenas removendo o parâmetro órfão
      window.history.replaceState(
        window.history.state,
        "",
        next ? `${window.location.pathname}?${next}` : window.location.pathname
      );
    }
  }, [comments, containerRef]);

  const toUuidArray = (arr: any): string[] | null => {
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const list = (Array.isArray(arr) ? arr : [])
      .map((m) => (typeof m === "string" ? m : m?.id || m?.userId || m?.value || ""))
      .filter((s) => UUID_RE.test(s));
    return list.length ? list : null;
  };

  async function handleSubmit({ text, mentions }: { text: string; mentions: any[]; files: File[] }) {
    if (!threadId || !text.trim()) return;
    setSending(true);
    try {
      const mencoes = toUuidArray(mentions);
        if (resource === "cronograma_subetapa") {
        await cronRepo.addComment({
          threadId,
          texto: text.trim(),
          mencoes: mencoes ?? [],
          arquivoUrl: null,
          arquivoNome: null,
          subetapaId: effectiveResourceId, // caso o repo aceite, mantém o vínculo explícito
          authorUsuarioId: currentUserId ?? null,
        });
      } else {
        await diarioRepo.addComment({
          threadId,
          texto: text.trim(),
          mencoes: mencoes ?? [],
          arquivoUrl: null,
            arquivoNome: null,
            authorUsuarioId: currentUserId ?? null,
        });
      }
      setText("");
      let data:
        | Awaited<ReturnType<typeof cronRepo.listComments>>
        | Awaited<ReturnType<typeof diarioRepo.listComments>>;
      if (resource === "cronograma_subetapa") {
        data = await cronRepo.listComments(threadId, { currentUsuarioId: currentUserId });
      } else {
        data = await diarioRepo.listComments(threadId, { currentUsuarioId: currentUserId });
      }
      const nextComments = (data ?? []) as diarioRepo.ComentarioRow[];
      setComments(nextComments);
      if (nextComments.length) {
        await loadLikes(nextComments.map((c) => c.id));
      }
      // recarrega likes dos novos ids
    } finally {
      setSending(false);
    }
  }

  return (
    <section
      className={
        tone === 'white'
          ? "rounded-xl border border-secondary bg-white p-4 shadow-sm"
          : `rounded-lg shadow-md border p-2 lg:p-3 transition-colors ${dark ? "bg-gray-800 border-gray-700" : "bg-background border-secondary"}`
      }
    >
      <header className={`text-base lg:text-lg font-ageo-bold mb-2 ${tone === 'white' ? "text-foreground" : dark ? "text-gray-100" : "text-foreground"}`}>
        Atualizações / Comentários
      </header>
      <div className="space-y-3" ref={containerRef}>
        {/* LISTA DE COMENTÁRIOS (mais antigos em cima, mais novos embaixo) */}
        <div className="space-y-2">
          {ordered.map((c) => {
            const anexos = c.arquivo_url ? [{ url: c.arquivo_url, nome: c.arquivo_nome ?? "arquivo" }] : [];
            return (
              <div key={c.id} data-comment-id={c.id}>
                <CommentCard
                  c={{
                    id: c.id,
                    authorId: c.created_by,
                    authorName: c.created_by && usersMap[c.created_by] ? usersMap[c.created_by] : "Usuário",
                    createdAt: c.created_at,
                    content: c.conteudo,
                    anexos,
                    likedByMe: !!likesMap[c.id]?.liked,
                    likesCount: likesMap[c.id]?.count ?? 0,
                  }}
                  canEdit={allowEdit && !!currentUserId && c.created_by === currentUserId}
                  canDelete={allowDelete && !!currentUserId && c.created_by === currentUserId}
                  onSaveEdit={(novo) =>
                    diarioRepo.updateComment(c.id, novo, {
                      editorUsuarioId: currentUserId ?? null,
                    })
                  }
                  onDelete={async () => {
                    await diarioRepo.softDeleteComment(c.id, {
                      deletedBy: currentUserId ?? null,
                    }); // mantém sua regra de negócio
                    setComments((prev) => prev.filter((x) => x.id !== c.id)); // remove localmente e evita qualquer 'salto'
                    // Se a URL estava apontando para ESSE comentário, limpa ?comentario= e "fixa" a rota
                    const qs = new URLSearchParams(window.location.search);
                    if (qs.get("comentario") === c.id) {
                      qs.delete("comentario");
                      const next = qs.toString();
                      window.history.replaceState(
                        window.history.state,
                        "",
                        next ? `${window.location.pathname}?${next}` : window.location.pathname
                      );
                    }
                  }}
                />
              </div>
            );
          })}
        </div>
        {/* EDITOR (embaixo) - só se criação permitida */}
        {allowCreate && (
          <div className="mt-4 w-full overflow-x-hidden">
            <DescricaoEditor
              value={text}
              onChange={setText}
              showToolbar
              submitOnEnter={false}
              sending={sending}
              sendLabel="Comentar"
              onSubmitWithPayload={handleSubmit}
              enableLocalSave
              draftKey={threadId ? `diario-comment-draft-${threadId}` : undefined}
              usuarios={mentionUsers}
            />
          </div>
        )}
      </div>
    </section>
  );
}
